import { entityIconFor } from '@gfe/core/services/entity-icon/entity-icon.service';
import { EntityLabelsTranslationService } from '@gfe/core/services/entity-labels-translation/entity-labels-translation.service';
import { ICONS } from '@gfe/core/services/icons/icons.service';
import { getFormatedDateTimeFromDate } from '@gfe/core/utils/date-utils';
import { isGreater, snapQuantity } from '@gfe/core/utils/quantity-utils';
import { compareAlphabetically, isNil } from '@gfe/core/utils/shared-utils';
import {
  addDurationFromDuration,
  addDurationFromIso,
  formatDuration,
  fromSecondsToDuration,
  parseISO8601Duration
} from '@gfe/core/utils/time-utils';
import { VisuItemLabel } from '@gfe/features/components/visualization/common/models/label.model';
import {
  List,
  ListItem
} from '@gfe/features/components/visualization/list/models/list-visualization';
import { GaliaMarker } from '@gfe/features/components/visualization/mapbox/models/galia-marker';
import { GaliaRoute } from '@gfe/features/components/visualization/mapbox/models/galia-route';
import { decodePolyline } from '@gfe/features/components/visualization/mapbox/utils/another-polyline-decoder';
import {
  Task,
  TaskKind,
  TaskType,
  TimelineRow
} from '@gfe/features/components/visualization/timeline/models/timeline';

import { VisualizationModule } from '@core/models/data-visualization/data-visualization.model';
import {
  AuxiliaryDataBundle,
  CascadeOutcome,
  EntityCfg,
  EntityInterface,
  EntityModificationAction,
  RegistrationRepairScope,
  TableColCfg
} from '@core/models/entities/entity-visualization-interface';
import {
  EntityDetailHandlers,
  GenericEntity,
  VisualizationBuilders
} from '@core/models/entities/generic-entity';
import { EntityCfgOverrides } from '@core/models/entities/generic-entity-cfg';
import { DateTimeWindow } from '@core/models/entities/implementations/time-window';
import { KNOWN_FEATURE_FLAGS } from '@core/models/feature-flags/known-feature-flags';
import { GenericEntityRegistryService } from '@core/services/user-models-registry/generic-entity-registry.service';

import { FilterDataType } from '@shared/components/filter-query-builder/filter.models';
import { TableCellDataType } from '@shared/components/table/table-cell/table-cell.component';
import {
  UidListIcon,
  UidListItem
} from '@shared/components/table/table-cell/uid-list/uid-list-item.model';
import { processWindowValue } from '@shared/components/table/table-cell/utils';

import { routeColor } from '../../colors.model';
import {
  KpiIds,
  KpiModel,
  RawKpi,
  arrayOfStrKpis,
  buildKpiMapKeyField,
  dbKpiId,
  isDurationKpi,
  keyValueMapKpis,
  kpiType,
  summariesForType
} from '../../kpi.model';
import { SplitEntityRow } from '../split-columns.model';
import { CustomData } from './custom-data';
import { measureUnitSymbol } from './measure-unit';
import {
  UserPlacement,
  isCompartmentPlacement,
  isVehiclePlacement
} from './route-action-occupants';
import {
  RouteActionStep,
  UserHolding,
  UserRouteActionCfg,
  UserRouteActionV1Model,
  computeTemperatureSegments,
  createActionLabels,
  createDriveTaskLabels,
  createStepDurationLabels,
  extractCompartmentUids,
  flattenHoldings,
  flattenMoves,
  foldTemperatureState,
  formatCapacityValue,
  getFirstServiceStartOrScheduledFirstStepStart,
  getHandledJobUids,
  getLastServiceFinish,
  getRouteActionType,
  getStopoverMarkerType,
  heldTemperatureUids,
  routeActionStepKindToTaskKind,
  warnUnresolvedStopoverSites
} from './user-route-action-v1';
import { UserSiteV1Model } from './user-site-v1';
import { UserVehicleV1Model } from './user-vehicle-v1';

export type RouteLockCfg =
  | 'Unlocked'
  | 'RemovalLockedOnly'
  | 'RemovalAndSequenceLocked'
  | 'RemovalInsertionAndSequenceLocked';

export type RouteTimingLockCfg = 'Free' | 'Locked';

export interface RouteLockOption {
  value: RouteLockCfg;
  labelKey: string;
  icon: string;
}

/**
 * The lock choices offered for a route, in the order they are shown. Read both by the
 * per-route context menu and by the toolbox's batch action, so the two cannot drift.
 */
export const ROUTE_LOCK_OPTIONS: readonly RouteLockOption[] = [
  {
    value: 'Unlocked',
    labelKey: 'visualization.timeline.context_menu.unlocked',
    icon: ICONS.lockOpen
  },
  {
    value: 'RemovalLockedOnly',
    labelKey: 'visualization.timeline.context_menu.removal_locked',
    icon: ICONS.lock
  },
  {
    value: 'RemovalInsertionAndSequenceLocked',
    labelKey: 'visualization.timeline.context_menu.removal_insertion_sequence_locked',
    icon: ICONS.lock
  }
];

export type RouteValidationState = 'Unvalidated' | 'Validated';

export interface RouteValidationOption {
  value: RouteValidationState;
  labelKey: string;
  icon: string;
}

/** The validation choices offered for a route, in the order they are shown. */
export const ROUTE_VALIDATION_OPTIONS: readonly RouteValidationOption[] = [
  {
    value: 'Validated',
    labelKey: 'visualization.timeline.context_menu.validate',
    icon: ICONS.checkCircle
  },
  {
    value: 'Unvalidated',
    labelKey: 'visualization.timeline.context_menu.unvalidate',
    icon: ICONS.xCircle
  }
];

export const ROUTE_VALIDATED_REASON_KEY =
  'visualization.timeline.context_menu.route_validated_blocked';

/** An absent state means `Unvalidated`. */
export function isRouteValidated(
  route: Pick<UserRouteV1Model, 'validation_state'>
): boolean {
  return route.validation_state === 'Validated';
}

/**
 * Translation key of the reason a route cannot be validated, or null when the front end
 * sees no obstacle. The company requirement is client-specific and enforced by the back end
 * alone, so it is deliberately not checked here.
 */
export function routeValidationBlocker(
  route: Pick<UserRouteV1Model, 'is_template'>
): string | null {
  return route.is_template
    ? 'visualization.timeline.context_menu.validate_blocked_template'
    : null;
}

type RouteTimelineState = {
  nextTaskId: number;
  lastDashedLine: boolean;
  /**
   * What the previous action left aboard, i.e. what travels during this action's drive leg.
   * `undefined` where the route offers no predecessor to read it from, which is not the same
   * claim as an empty vehicle.
   */
  holdingsAboard: UserHolding[] | undefined;
  /**
   * The temperature each placement is in, as `Temperature` holdings. Carried rather than
   * re-read per action: a placement stays in its temperature until a `Replace` changes it,
   * and the wire stops reporting the holding once the placement is emptied.
   */
  temperatureState: UserHolding[];
  lastCompartmentsUids: string[];
};

/**
 * A registration slot on a route. The backend schema is a strict
 * `oneOf [{ code }, { copy }]` with `additionalProperties: false`, so a writer must set
 * exactly one of the two; this reader-friendly shape keeps both accessible.
 */
export interface UserRegistrationAssignment {
  code?: string;
  copy?: number;
}

export type FleetKind = 'Vehicle' | 'Trailer';

export type StockEventKind = 'Departure' | 'HomeRestRelease' | 'HomeRestRetake' | 'Return';

export interface UserFleetHandoverEnd {
  route_uid: string;
  event: StockEventKind;
  at: string;
}

/** A vehicle or trailer passing from the `from` route to the `to` route at `site_uid`. */
export interface UserFleetHandover {
  fleet_kind: FleetKind;
  site_uid: string;
  from: UserFleetHandoverEnd;
  to: UserFleetHandoverEnd;
}

export interface UserRouteV1Model {
  uid: string;
  name?: string;
  vehicle_uid: string;
  trailer_uid?: string;
  driver_uid?: string;
  company_uid?: string;
  lock_cfg: RouteLockCfg;
  /** Absent means `Free`. */
  timing_lock?: RouteTimingLockCfg;
  /** DL's serde names for its `UserValidationState`; absent means `Unvalidated`. */
  validation_state?: RouteValidationState;
  is_template?: boolean;
  /** Display color as `#RRGGBB`. */
  color?: string | null;
  actions: UserRouteActionV1Model[];
  kpis: RawKpi[];
  custom_data: CustomData;
  fleet_handovers?: UserFleetHandover[];
  vehicle_registration: UserRegistrationAssignment | null;
  trailer_registration?: UserRegistrationAssignment | null;
  driver_registration?: UserRegistrationAssignment | null;
  scheduled_start_datetime?: string;
  // Pre-computed values for performance
  beginEndOfRoute?: string;
  formattedKpis?: Record<string, unknown>;
}

export const DEFAULT_ROUTE_COLOR = '#a1a1a1';

function vehicleIsEmpty(action: UserRouteActionV1Model): boolean {
  return !flattenHoldings(action.holdings).some(
    (holding) => !isNil(holding.quantity) && holding.quantity > 1e-10
  );
}

export function getRouteSiteColor(
  siteUid: string,
  auxiliarySites: Record<string, UserSiteV1Model>
): string {
  return auxiliarySites[siteUid]?.color ?? DEFAULT_ROUTE_COLOR;
}

function computeColorSegments(
  action: UserRouteActionV1Model,
  pickupSiteUidOfJob: Record<string, string>,
  auxiliarySites: Record<string, UserSiteV1Model>
): { color: string; percentage: number }[] | undefined {
  const siteColor = getRouteSiteColor(action.site_uid, auxiliarySites);

  if (siteColor !== DEFAULT_ROUTE_COLOR) {
    return [{ color: siteColor, percentage: 100.0 }];
  }

  if (getRouteActionType(action).includes('delivery')) {
    let totalQuantity = 0;
    const quantitiesByPickupSite: Record<string, number> = {};

    flattenMoves(action.movements)
      .filter(
        (f) =>
          f.kind === 'unload' &&
          f.axis === 'capacity' &&
          f.quantity > 1e-10 &&
          !isNil(f.job_uid)
      )
      .forEach((f) => {
        const pickupSiteUid = pickupSiteUidOfJob[f.job_uid as string];
        quantitiesByPickupSite[pickupSiteUid] =
          (quantitiesByPickupSite[pickupSiteUid] || 0) + f.quantity;
        totalQuantity += f.quantity;
      });

    return Object.entries(quantitiesByPickupSite).map(([pickupSiteUid, quantity]) => ({
      color: getRouteSiteColor(pickupSiteUid, auxiliarySites),
      percentage: 100.0 * (quantity / totalQuantity)
    }));
  }

  return undefined;
}

function createRouteKpiLabels(kpis: RawKpi[]): VisuItemLabel[] {
  const labels: VisuItemLabel[] = [];

  const loadedCaps = kpis.find((kpi) => kpi.id === 'total_load_capacities');
  if (!isNil(loadedCaps)) {
    Object.entries(loadedCaps.value as Record<string, number>)
      .sort(([capUidA], [capUidB]) => compareAlphabetically(capUidA, capUidB))
      .forEach(([capUid, value]) => {
        if (isGreater(value, 0)) {
          labels.push({
            label: 'total_loaded',
            level: 0,
            value: formatCapacityValue(value) + ' ' + capUid
          });
        }
      });
  }

  const distanceKpi = kpis.find((kpi) => kpi.id === 'travel_distance_m');
  if (!isNil(distanceKpi)) {
    labels.push({
      label: 'travel_distance',
      level: 0,
      value: ((distanceKpi.value as number) / 1000).toFixed(1) + ' km'
    });
  }

  const durationKpi = kpis.find((kpi) => kpi.id === 'total_duration_s');
  if (!isNil(durationKpi)) {
    labels.push({
      label: 'duration',
      level: 0,
      value: formatDuration(fromSecondsToDuration(durationKpi.value as number))
    });
  }

  const nbJobsKpi = kpis.find((kpi) => kpi.id === 'nb_jobs');
  if (!isNil(nbJobsKpi)) {
    labels.push({ label: 'jobs', level: 0, value: String(nbJobsKpi.value) });
  }

  const nbStopsKpi = kpis.find((kpi) => kpi.id === 'nb_stops');
  if (!isNil(nbStopsKpi)) {
    labels.push({ label: 'nb_stops', level: 0, value: String(nbStopsKpi.value) });
  }

  return labels;
}

function computeFormattedKpis(kpis: RawKpi[]): Record<string, unknown> {
  const formattedKpis: Record<string, unknown> = {};

  // KPIs that should be transformed from map to list
  const shouldTransformToList = new Set([
    'total_load_capacities',
    'assigned_jobs',
    'peak_capacities',
    'average_capacities'
  ]);

  kpis.forEach((rawKpi) => {
    const kpiModel = new KpiModel(rawKpi);
    if (kpiModel.isMapType()) {
      const map = kpiModel.getformattedMap();
      if (shouldTransformToList.has(rawKpi.id)) {
        // Transform map to list of "${value} ${cap_name}", sorted by capacity
        // name so capacity labels appear in a consistent alphabetical order.
        formattedKpis[rawKpi.id] = Object.entries(map)
          .sort(([capNameA], [capNameB]) => compareAlphabetically(capNameA, capNameB))
          .map(([cap_name, value]) => '' + value + ' ' + cap_name);
      } else {
        formattedKpis[rawKpi.id] = map;
      }
    } else if (kpiModel.isArrayType()) {
      formattedKpis[rawKpi.id] = kpiModel.getformattedArray();
    } else {
      formattedKpis[rawKpi.id] = kpiModel.value;
    }
  });

  return formattedKpis;
}

function routeStart(raw: UserRouteV1Model): Date | undefined {
  if (!isNil(raw.scheduled_start_datetime)) {
    return new Date(raw.scheduled_start_datetime);
  }
  const firstAction = raw.actions?.[0]?.scheduled_first_step_start_datetime;
  return isNil(firstAction) ? undefined : new Date(firstAction);
}

function computeBeginEndOfRoute(raw: UserRouteV1Model): string {
  const begin = routeStart(raw);
  if (isNil(begin)) {
    return '';
  }

  const timeToEnd = raw.kpis.find((k) => k.id === 'total_duration_s')?.value as number;

  if (isNil(timeToEnd)) {
    return '';
  }

  const end = addDurationFromDuration(begin, fromSecondsToDuration(timeToEnd));

  const timeWindow: DateTimeWindow = {
    start: begin.toISOString(),
    finish: end.toISOString()
  };

  return processWindowValue(timeWindow, 'datetime');
}

function makeStepName(
  action: UserRouteActionV1Model,
  step: RouteActionStep,
  vehiculeEmpty?: boolean
): string {
  const prefix = action.site_uid + ': ';
  if (step.kind === 'ActiveService') {
    const flat = flattenMoves(action.movements);
    const hasPickup = flat.some((f) => f.kind === 'load' && f.quantity > 0.0);
    const hasDelivery = flat.some((f) => f.kind === 'unload' && f.quantity > 0.0);
    if (hasPickup && hasDelivery) {
      return (
        prefix +
        EntityLabelsTranslationService.translateRouteActionStatic('pickup_delivery')
      );
    }
    if (hasPickup) {
      return prefix + EntityLabelsTranslationService.translateRouteActionStatic('pickup');
    } else if (hasDelivery) {
      return prefix + EntityLabelsTranslationService.translateRouteActionStatic('delivery');
    }
    return prefix + EntityLabelsTranslationService.translateRouteActionStatic('service');
  } else if (step.kind === 'Drive' && !isNil(vehiculeEmpty)) {
    return EntityLabelsTranslationService.translateRouteActionStatic(
      vehiculeEmpty ? 'drive_empty' : 'drive_loaded'
    );
  }
  return prefix + EntityLabelsTranslationService.translateRouteActionStatic(step.kind);
}

function registrationSlot(
  registration: UserRegistrationAssignment | null | undefined
): string | number | undefined {
  if (isNil(registration)) return undefined;
  return registration.copy ?? registration.code;
}

export function computeRouteName(
  route: UserRouteV1Model,
  overrides: {
    vehicleUid?: string;
    companyUid?: string | null;
    registration?: UserRegistrationAssignment | null;
  } = {}
): string {
  const vehicleUid = overrides.vehicleUid ?? route.vehicle_uid;
  const companyUid = 'companyUid' in overrides ? overrides.companyUid : route.company_uid;
  const registration =
    'registration' in overrides ? overrides.registration : route.vehicle_registration;

  const slot = registrationSlot(registration);
  const segments: (string | number)[] = [vehicleUid];
  if (companyUid) segments.push(companyUid);
  if (!isNil(slot)) segments.push(slot);
  segments.push(route.uid);

  return segments.join('-');
}

const REASSIGN_KEY_PREFIX = 'visualization.timeline.context_menu.vehicle_reassign_';

type CarryOver =
  | { kind: 'keep' }
  | { kind: 'replace'; value: string }
  | { kind: 'abort'; reason: 'ambiguous' | 'none' };

/**
 * Cases are ordered and first-match-wins: an unassigned resource stays unassigned even
 * when the new vehicle has exactly one compatible option, so a reassignment never invents
 * an assignment that was not there. A null `compatible` list means unrestricted.
 */
function carryOver(
  current: string | null | undefined,
  compatible: string[] | null | undefined
): CarryOver {
  if (isNil(current)) return { kind: 'keep' };
  if (isNil(compatible)) return { kind: 'keep' };
  if (compatible.includes(current)) return { kind: 'keep' };
  if (compatible.length === 1) return { kind: 'replace', value: compatible[0] };
  return { kind: 'abort', reason: compatible.length === 0 ? 'none' : 'ambiguous' };
}

function sameStringArray(actual: unknown, expected: string[]): boolean {
  return (
    Array.isArray(actual) &&
    actual.length === expected.length &&
    actual.every((value, index) => value === expected[index])
  );
}

/**
 * Returns the full kpis array with `id`'s entry replaced, or null when there is nothing to
 * write. Callers must send the whole array because a PUT replaces it wholesale.
 */
export function replaceStringArrayKpi(
  kpis: RawKpi[] | undefined,
  id: string,
  value: string[]
): RawKpi[] | null {
  if (isNil(kpis)) return null;

  const index = kpis.findIndex((kpi) => kpi.id === id);
  if (index < 0) return null;
  if (sameStringArray(kpis[index].value, value)) return null;

  return kpis.map((kpi, i) => (i === index ? { ...kpi, value } : kpi));
}

export function hasFleetHandovers(route: UserRouteV1Model): boolean {
  return (route.fleet_handovers?.length ?? 0) > 0;
}

/** `out`: this route hands units to the other one; `in`: it receives them; `both`: a relay. */
export type HandoverDirection = 'out' | 'in' | 'both';

export interface HandoverGroup {
  otherRouteUid: string;
  direction: HandoverDirection;
  fleetKinds: FleetKind[];
  handovers: UserFleetHandover[];
}

const FLEET_KIND_ORDER: FleetKind[] = ['Vehicle', 'Trailer'];

/**
 * One group per route this route shares a unit with, in order of first appearance. Every
 * handover on a route has the route itself at one end; one naming it at both ends, or at
 * neither, has no other route to show and is skipped.
 */
export function groupHandoversByRoute(route: UserRouteV1Model): HandoverGroup[] {
  const groups = new Map<string, HandoverGroup>();

  for (const handover of route.fleet_handovers ?? []) {
    const givesAway = handover.from.route_uid === route.uid;
    const receives = handover.to.route_uid === route.uid;
    if (givesAway === receives) continue;

    const otherRouteUid = givesAway ? handover.to.route_uid : handover.from.route_uid;
    const direction: HandoverDirection = givesAway ? 'out' : 'in';
    const group = groups.get(otherRouteUid);
    if (isNil(group)) {
      groups.set(otherRouteUid, {
        otherRouteUid,
        direction,
        fleetKinds: [handover.fleet_kind],
        handovers: [handover]
      });
      continue;
    }

    group.handovers.push(handover);
    if (group.direction !== direction) group.direction = 'both';
    group.fleetKinds = FLEET_KIND_ORDER.filter(
      (kind) => kind === handover.fleet_kind || group.fleetKinds.includes(kind)
    );
  }

  return [...groups.values()];
}

export function affectedJobUids(route: UserRouteV1Model): string[] {
  return [...new Set((route.actions ?? []).flatMap((action) => getHandledJobUids(action)))];
}

export function deriveJobKpiPatch(
  job: { uid: string; kpis?: RawKpi[] },
  kpiId: string,
  value: string[]
): Record<string, unknown> | null {
  const kpis = replaceStringArrayKpi(job.kpis, kpiId, value);
  if (isNil(kpis)) return null;

  return { uid: job.uid, kpis };
}

/** Every placement the route's actions name, movements and holdings alike. */
function routePlacements(route: UserRouteV1Model): UserPlacement[] {
  return (route.actions ?? []).flatMap((action) => [
    ...(action.movements ?? [])
      .map((movement) => movement.placement)
      .filter((placement): placement is UserPlacement => !isNil(placement)),
    ...(action.holdings ?? []).map((holding) => holding.placement)
  ]);
}

/**
 * Retargets one placement at the route's new resources. A vehicle placement names the route's
 * vehicle, so it always follows the reassignment; a trailer or compartment placement names the
 * trailer, so it follows only when the trailer itself changed. Returns the placement unchanged
 * (same reference) when it names neither, which is how the caller detects a no-op.
 */
function reassignPlacement(
  placement: UserPlacement,
  vehicleUid: string,
  newTrailerUid: string | undefined
): UserPlacement {
  if (isVehiclePlacement(placement)) {
    return placement.vehicle_uid === vehicleUid
      ? placement
      : { ...placement, vehicle_uid: vehicleUid };
  }
  if (isNil(newTrailerUid) || placement.trailer_uid === newTrailerUid) return placement;
  return { ...placement, trailer_uid: newTrailerUid };
}

/**
 * The actions with every placement retargeted, or null when none named the old resources.
 * Actions are copied field-by-field from the raw so fields this model does not declare
 * (`synchronized_actions`, for one) survive the round trip: a PATCH replaces the array
 * wholesale, so whatever is omitted here is erased.
 */
function reassignActionPlacements(
  route: UserRouteV1Model,
  vehicleUid: string,
  newTrailerUid: string | undefined
): UserRouteActionV1Model[] | null {
  let changed = false;
  const retarget = (placement: UserPlacement): UserPlacement => {
    const next = reassignPlacement(placement, vehicleUid, newTrailerUid);
    if (next !== placement) changed = true;
    return next;
  };

  const actions = (route.actions ?? []).map((action) => {
    const next: UserRouteActionV1Model = { ...action };
    if (!isNil(action.movements)) {
      next.movements = action.movements.map((movement) =>
        isNil(movement.placement)
          ? movement
          : { ...movement, placement: retarget(movement.placement) }
      );
    }
    if (!isNil(action.holdings)) {
      next.holdings = action.holdings.map((holding) => ({
        ...holding,
        placement: retarget(holding.placement)
      }));
    }
    return next;
  });

  return changed ? actions : null;
}

export function deriveRouteVehicleReassignment(
  route: UserRouteV1Model,
  vehicle: UserVehicleV1Model
): CascadeOutcome {
  const params = { vehicle: vehicle.uid };

  const trailer = carryOver(route.trailer_uid, vehicle.compatible_trailer_uids);
  if (trailer.kind === 'abort') {
    const key = trailer.reason === 'none' ? 'no_compatible_trailer' : 'ambiguous_trailer';
    return { abortReasonKey: `${REASSIGN_KEY_PREFIX}${key}`, params };
  }

  const driver = carryOver(route.driver_uid, vehicle.compatible_driver_uids);
  if (driver.kind === 'abort') {
    const key = driver.reason === 'none' ? 'no_compatible_driver' : 'ambiguous_driver';
    return { abortReasonKey: `${REASSIGN_KEY_PREFIX}${key}`, params };
  }

  // A compartment placement names a compartment *of the route's trailer*, and the new
  // trailer need not offer the same ones. Retargeting the trailer uid alone would leave the
  // route loading goods into a compartment that may not exist, and nothing available here
  // can map the old compartments onto the new trailer's.
  if (trailer.kind === 'replace' && routePlacements(route).some(isCompartmentPlacement)) {
    return {
      abortReasonKey: `${REASSIGN_KEY_PREFIX}trailer_compartments`,
      params
    };
  }

  const fields: Record<string, unknown> = {
    uid: route.uid,
    vehicle_uid: vehicle.uid,
    vehicle_registration: null,
    name: computeRouteName(route, { vehicleUid: vehicle.uid, registration: null })
  };

  if (trailer.kind === 'replace') {
    fields['trailer_uid'] = trailer.value;
    fields['trailer_registration'] = null;
  }
  if (driver.kind === 'replace') {
    fields['driver_uid'] = driver.value;
    fields['driver_registration'] = null;
  }

  const actions = reassignActionPlacements(
    route,
    vehicle.uid,
    trailer.kind === 'replace' ? trailer.value : undefined
  );
  if (!isNil(actions)) {
    fields['actions'] = actions;
  }

  const kpis = replaceStringArrayKpi(route.kpis, 'assigned_vehicles', [vehicle.uid]);
  if (!isNil(kpis)) {
    fields['kpis'] = kpis;
  }

  // `replace` is only reachable for a resource the route actually had, so the current uid is
  // present whenever the new one differs. An unchanged uid moves no slot and stays out of scope.
  const repairScope: RegistrationRepairScope = {
    routeUid: route.uid,
    vehicleUids: [route.vehicle_uid, vehicle.uid],
    trailerUids:
      trailer.kind === 'replace' ? [route.trailer_uid as string, trailer.value] : [],
    driverUids: driver.kind === 'replace' ? [route.driver_uid as string, driver.value] : []
  };

  return { fields, repairScope };
}

function createStepLabels(
  step: RouteActionStep,
  action: UserRouteActionV1Model,
  activeServiceLabels: VisuItemLabel[],
  lastTemperatureUids: string[],
  holdingsAboard: UserHolding[]
): VisuItemLabel[] {
  const actionStepKind = routeActionStepKindToTaskKind(step.kind);

  if (step.kind === 'ActiveService') return activeServiceLabels;
  if (actionStepKind === 'drive')
    return createDriveTaskLabels(step, action, lastTemperatureUids, holdingsAboard);
  if (['wait', 'pause', 'home-rest'].includes(actionStepKind)) {
    return createStepDurationLabels(step);
  }
  return [];
}

function createTaskFromStep(
  step: RouteActionStep,
  action: UserRouteActionV1Model,
  state: RouteTimelineState,
  currentDate: Date,
  activeServiceLabels: VisuItemLabel[],
  dashedLine: boolean,
  currentCompartmentUids: string[],
  actionTemperatureState: UserHolding[],
  pickupSiteUidOfJob: Record<string, string>,
  auxiliarySites: Record<string, UserSiteV1Model>,
  routeUid: string,
  stepIndex: number,
  actionIndex: number
): Task {
  const isActiveService = step.kind === 'ActiveService';
  const endDate = addDurationFromIso(currentDate, step.duration);
  // Drive/wait steps happen before this action's own service, so what's aboard during
  // them is what the prior action left behind (state.*), not this action's holdings.
  // The service step itself is when this action's own load/unload takes effect, so it
  // must reflect action.holdings directly - same source the action's own labels use -
  // rather than the still-stale state carried in from before the service ran.
  const labels = createStepLabels(
    step,
    action,
    activeServiceLabels,
    heldTemperatureUids(state.temperatureState),
    state.holdingsAboard ?? []
  );
  const colorSegments = isActiveService
    ? computeColorSegments(action, pickupSiteUidOfJob, auxiliarySites)
    : undefined;
  const temperatureState = isActiveService
    ? actionTemperatureState
    : state.temperatureState;
  const temperatureUids = heldTemperatureUids(temperatureState);
  // Same split as the labels above: the cargo that decides which temperature lines are drawn
  // dashed is this action's own only on its service step, and the predecessor's before it.
  const temperatureSegments = computeTemperatureSegments(
    temperatureState,
    isActiveService ? action.holdings : state.holdingsAboard
  );
  const stepDashedLine = isActiveService ? dashedLine : state.lastDashedLine;

  return {
    id: state.nextTaskId,
    siteUid: action.site_uid,
    kind: routeActionStepKindToTaskKind(step.kind),
    type: getRouteActionType(action),
    name: makeStepName(action, step, stepDashedLine),
    dashedLine: stepDashedLine,
    startDate: currentDate,
    endDate,
    labels,
    highlightAggregatorIds: [],
    temperatureUids,
    temperatureSegments,
    compartmentUids: isActiveService ? currentCompartmentUids : state.lastCompartmentsUids,
    colorSegments,
    routeUid,
    actionUid: action.uid,
    stepIndex,
    actionIndex,
    jobUids: getHandledJobUids(action)
  };
}

/**
 * One numeric column per in-scope capacity resource, each reading the route's
 * `total_load_capacities` KPI map by capacity uid. Returns [] when no capacities are available
 * so the caller keeps the combined column as a fallback. Each column is sorted/filtered
 * server-side via the KPI-map-key field `#assoc:kpi.total_load_capacities[<uid>]`; a capacity
 * whose uid falls outside the backend-accepted charset stays display-only.
 */
function buildLoadedCapacityColumns(
  capacities: SplitEntityRow[]
): TableColCfg<EntityInterface<UserRouteV1Model>>[] {
  if (capacities.length === 0) return [];
  const prefix =
    EntityLabelsTranslationService.translateLabelStatic('table.columns.loaded');
  return capacities.map((cap) => {
    const capName = cap.name ?? cap.uid;
    const readValue = (row?: EntityInterface<UserRouteV1Model>): number | null => {
      const route = row?.raw;
      if (isNil(route)) return null;
      const map = route.kpis.find((k) => k.id === 'total_load_capacities')?.value as
        | Record<string, number>
        | undefined;
      const value = map?.[cap.uid];
      return typeof value === 'number' && isGreater(value, 0) ? value : null;
    };
    const col: TableColCfg<EntityInterface<UserRouteV1Model>> = {
      key: `raw.kpis.total_load_capacities.${cap.uid}` as keyof EntityInterface<UserRouteV1Model>,
      label: `${prefix} ${capName}`,
      staticLabel: true,
      type: 'number',
      unit: measureUnitSymbol(cap.unit),
      summaryTypes: ['sum', 'avg', 'max', 'min'],
      rawValue: (row?: EntityInterface<UserRouteV1Model>): unknown => readValue(row),
      renderHook: (_value: unknown, row?: EntityInterface<UserRouteV1Model>): unknown => {
        const value = readValue(row);
        return isNil(value) ? null : snapQuantity(value);
      }
    };

    const field = buildKpiMapKeyField('total_load_capacities', cap.uid);
    if (field) {
      col.sortCfg = { dbColName: field };
      col.filterCfg = {
        translationLabel: cap.uid,
        displayLabel: `${prefix} ${capName}`,
        dbColName: field,
        filterType: FilterDataType.NUMBER
      };
    }

    return col;
  });
}

const DIRECTION_ICONS: Record<HandoverDirection, string> = {
  out: ICONS.arrowRight,
  in: ICONS.arrowLeft,
  both: ICONS.arrowsLeftRight
};

const FLEET_KIND_ENTITY: Record<FleetKind, string> = {
  Vehicle: 'vehicle',
  Trailer: 'trailer'
};

/** One pill per other route: which way the units go, which kinds, and every handover on hover. */
function handoverPills(route: UserRouteV1Model): UidListItem[] {
  return groupHandoversByRoute(route).map((group) => ({
    uid: group.otherRouteUid,
    icons: [
      { iconClass: DIRECTION_ICONS[group.direction], tone: 'accent' },
      ...group.fleetKinds.map(
        (kind): UidListIcon => ({
          iconClass: entityIconFor(FLEET_KIND_ENTITY[kind]),
          tone: 'muted'
        })
      )
    ],
    tooltip: { title: group.otherRouteUid, lines: group.handovers.map(describeHandover) }
  }));
}

function describeHandover(handover: UserFleetHandover): string {
  const translate = EntityLabelsTranslationService.translateLabelStatic;
  const end = (side: UserFleetHandoverEnd) =>
    `${side.route_uid} ${translate(`fleet_handovers.event.${side.event}`)} ` +
    getFormatedDateTimeFromDate(new Date(side.at));
  return (
    `${translate(`fleet_handovers.kind.${handover.fleet_kind}`)} · ${handover.site_uid}: ` +
    `${end(handover.from)} → ${end(handover.to)}`
  );
}

export const routeCfgOverrides: EntityCfgOverrides<UserRouteV1Model> = {
  defaultSortingCol: ['name'],
  // The deployed schema does not declare the occupation module yet; offering it from
  // the front end keeps the visualizations the schema does declare.
  extraVisualizations: new Set<VisualizationModule>(['occupation']),
  availableDetailEntityType: ['route_action'],
  splitColumnTargetVersionNames: ['UserCapacityResource'],
  preprocessEntity: (raw: UserRouteV1Model): UserRouteV1Model => ({
    ...raw,
    beginEndOfRoute: computeBeginEndOfRoute(raw),
    formattedKpis: computeFormattedKpis(raw.kpis)
  }),
  buildColumns: (
    autoColumns: TableColCfg<EntityInterface<UserRouteV1Model>>[],
    getSplitEntities?: (targetVersionName: string) => SplitEntityRow[]
  ): TableColCfg<EntityInterface<UserRouteV1Model>>[] => {
    // DURATION

    const beginEndOfRouteColumn: TableColCfg<EntityInterface<UserRouteV1Model>> = {
      key: 'raw.beginEndOfRoute' as keyof EntityInterface<UserRouteV1Model>,
      label: 'table.columns.begin_end_of_route',
      type: 'string'
    };

    // KPI

    const distanceKmRenderFn = (value: unknown) => ((value as number) / 1000).toFixed(1);
    const timeRenderFn = (value: unknown) =>
      formatDuration(fromSecondsToDuration(value as number));

    const kpiDefs: {
      id: KpiIds;
      renderSummaryFn?: (value: unknown) => unknown;
      filterable?: boolean;
      entityType?: string;
      type?: TableCellDataType;
      should_transform_to_list?: boolean;
      /** Renders the KPI as a number cell carrying this unit. */
      unit?: string;
      /** A list-valued KPI has no meaningful order, so its column offers no sort. */
      sortable?: boolean;
    }[] = [
      {
        id: 'pickup_sites',
        type: 'uid-list',
        filterable: true,
        entityType: 'SITE',
        sortable: false
      },
      {
        id: 'delivery_sites',
        type: 'uid-list',
        filterable: true,
        entityType: 'SITE',
        sortable: false
      },
      { id: 'total_load_capacities', should_transform_to_list: true },
      {
        id: 'assigned_jobs',
        should_transform_to_list: true,
        entityType: 'JOB',
        sortable: false
      },
      { id: 'travel_distance_m', renderSummaryFn: distanceKmRenderFn, unit: 'km' },
      { id: 'total_duration_s', renderSummaryFn: timeRenderFn },
      { id: 'travel_duration_s', renderSummaryFn: timeRenderFn },
      { id: 'service_duration_s', renderSummaryFn: timeRenderFn },
      { id: 'pause_duration_s', renderSummaryFn: timeRenderFn },
      { id: 'wait_duration_s', renderSummaryFn: timeRenderFn },
      { id: 'nb_jobs' },
      { id: 'nb_pick_stops' },
      { id: 'nb_stops' },
      { id: 'operational_cost' },
      { id: 'peak_capacities', should_transform_to_list: true, sortable: false },
      { id: 'average_capacities', should_transform_to_list: true, sortable: false },
      {
        id: 'empty_travel_distance_m',
        renderSummaryFn: distanceKmRenderFn,
        unit: 'km'
      },
      { id: 'rewards' }
    ];

    const kpiCols: TableColCfg<EntityInterface<UserRouteV1Model>>[] = kpiDefs.map((kpi) => {
      const type: TableCellDataType = kpi.unit
        ? 'number'
        : kpi.should_transform_to_list
          ? 'uid-list'
          : keyValueMapKpis.has(kpi.id)
            ? 'string-number-map'
            : arrayOfStrKpis.has(kpi.id)
              ? 'uid-list'
              : 'string';
      const summaryTypes = summariesForType[kpiType[kpi.id]];

      const col: TableColCfg<EntityInterface<UserRouteV1Model>> = {
        key: ('raw.kpis.' + kpi.id) as keyof EntityInterface<UserRouteV1Model>,
        label: 'kpis.' + kpi.id,
        type,
        unit: kpi.unit,
        // The cell type stays `string` because the renderHook serves the already
        // formatted KPI value; the flag is what tells the export these seconds
        // KPIs (the `_s` suffix, as in Kpi.value) are durations.
        isDuration: isDurationKpi(kpi.id),
        rawValue(row?: EntityInterface<UserRouteV1Model>): unknown {
          const route = row?.raw;
          if (isNil(route)) {
            return undefined;
          }
          // A route need not carry every KPI the columns declare, and the summary row reads
          // this for every KPI column of every loaded route: an absent KPI is a value the
          // summary leaves out, not an error.
          return route.kpis.find((k) => k.id === kpi.id)?.value;
        },
        renderHook(_value: unknown, row?: EntityInterface<UserRouteV1Model>) {
          const route = row?.raw;
          const emptyValue = type === 'string' || type === 'number' ? '' : {};
          if (isNil(route) || isNil(route.formattedKpis)) return emptyValue;
          return route.formattedKpis[kpi.id] ?? emptyValue;
        },
        renderSummary: kpi.renderSummaryFn,
        sortCfg:
          kpi.sortable === false
            ? undefined
            : { dbColName: '#assoc:kpi.' + (dbKpiId[kpi.id] ?? kpi.id) },
        summaryTypes
      };

      if (kpi.filterable) {
        const filterType =
          kpi.type === 'uid-list' ? FilterDataType.UID_LIST : FilterDataType.NUMBER;
        col.filterCfg = {
          translationLabel: dbKpiId[kpi.id] ?? kpi.id,
          dbColName: `#assoc:kpi.${dbKpiId[kpi.id] ?? kpi.id}`,
          filterType,
          uidEntityType: kpi.entityType
        };
      }

      // Surface the reference entity type so the uid/name toggle and popover work
      // even for non-filterable reference KPIs (e.g. assigned_jobs), which never
      // get a filterCfg to carry it.
      if (kpi.entityType) {
        col.referenceEntityType = kpi.entityType;
      }

      return col;
    });

    const capacities = getSplitEntities?.('UserCapacityResource') ?? [];
    const splitCapacityCols = buildLoadedCapacityColumns(capacities);

    let finalKpiCols = kpiCols;
    if (splitCapacityCols.length > 0) {
      const idx = kpiCols.findIndex(
        (c) => (c.key as string) === 'raw.kpis.total_load_capacities'
      );
      finalKpiCols =
        idx >= 0
          ? [...kpiCols.slice(0, idx), ...splitCapacityCols, ...kpiCols.slice(idx + 1)]
          : [...kpiCols, ...splitCapacityCols];
    }

    const fleetHandoversColumn: TableColCfg<EntityInterface<UserRouteV1Model>> = {
      key: 'raw.fleet_handovers' as keyof EntityInterface<UserRouteV1Model>,
      label: 'table.columns.fleet_handovers',
      type: 'uid-list',
      referenceEntityType: 'ROUTE',
      readonly: true,
      renderHook: (_value: unknown, row?: EntityInterface<UserRouteV1Model>) =>
        isNil(row?.raw) ? [] : handoverPills(row.raw)
    };

    const columns = autoColumns.map((col) => {
      // A uid filter field is what lets "Include in filter" show and save its condition.
      if (String(col.key) === 'raw.uid') {
        return {
          ...col,
          filterCfg: {
            translationLabel: 'uid',
            dbColName: 'uid',
            filterType: FilterDataType.UID_LIST,
            uidEntityType: 'ROUTE'
          }
        };
      }
      // A yes/no badge, like the template column. Read-only: the badge renders a boolean, so an
      // inline edit would write true/false into the field; validating goes through its actions.
      if (String(col.key) === 'raw.validation_state') {
        return {
          ...col,
          type: 'boolean' as const,
          readonly: true,
          renderHook: (value: unknown) => value === 'Validated'
        };
      }
      return col;
    });

    return [...columns, fleetHandoversColumn, beginEndOfRouteColumn, ...finalKpiCols];
  }
};

/**
 * A route DL has not framed yet, such as one an edit session just opened. Its row has no task, and
 * its dates, which nothing on it occupies, are left out of the timeline's span.
 */
function emptyTimelineRow(raw: UserRouteV1Model): TimelineRow {
  const at = raw.scheduled_start_datetime
    ? new Date(raw.scheduled_start_datetime)
    : new Date();
  return {
    uid: raw.uid,
    color: routeColor(raw.uid, raw.color),
    startDate: at,
    endDate: at,
    labels: createRouteKpiLabels(raw.kpis),
    lockCfg: raw.lock_cfg,
    route: raw,
    validated: isRouteValidated(raw),
    name: raw.name ?? '-',
    tasks: []
  };
}

export const routeVisualizationBuilders: VisualizationBuilders<UserRouteV1Model> = {
  buildMarkers: (raw: UserRouteV1Model, auxData?: AuxiliaryDataBundle): GaliaMarker[] => {
    const color = routeColor(raw.uid, raw.color);

    const markers: GaliaMarker[] = [];
    const auxiliarySites: Record<string, UserSiteV1Model> = Object.fromEntries(
      ((auxData?.entitiesArrayPerVersionName['UserSite'] ?? []) as UserSiteV1Model[]).map(
        (site) => [site.uid, site]
      )
    );

    const unresolvedSiteUids: string[] = [];

    for (const action of raw.actions) {
      const coords = auxiliarySites[action.site_uid]?.coords;

      if (isNil(coords)) {
        unresolvedSiteUids.push(action.site_uid);
        continue;
      }

      const labels = createActionLabels(action, raw.uid, raw.vehicle_uid);
      markers.push({
        uid: action.uid,
        title: action.uid,
        coords,
        color,
        type: getStopoverMarkerType(action, raw.actions.length),
        description: {
          name: action.uid,
          startDate: getFirstServiceStartOrScheduledFirstStepStart(action),
          endDate: getLastServiceFinish(action),
          content: labels
        },
        structuralAggregatorIds: [raw.uid],
        jobUids: getHandledJobUids(action)
      });
    }
    warnUnresolvedStopoverSites(raw.uid, unresolvedSiteUids);
    return markers;
  },

  buildRoutes: (raw: UserRouteV1Model): GaliaRoute[] => {
    const color = routeColor(raw.uid, raw.color);
    const coords = raw.actions.flatMap((action: UserRouteActionV1Model) => {
      const polyline = action.polyline;
      return !isNil(polyline) ? decodePolyline(polyline) : [];
    });
    return [{ color, coords, name: raw.uid, uid: raw.uid, width: 5 }];
  },

  buildTimelineRows: (
    raw: UserRouteV1Model,
    auxData?: AuxiliaryDataBundle
  ): TimelineRow[] => {
    if (raw.actions.length <= 0) return [emptyTimelineRow(raw)];

    const DEFAULT_STEP: RouteActionStep = { kind: 'ActiveService', duration: 'PT0S' };
    const getSteps = (act: UserRouteActionV1Model): RouteActionStep[] =>
      act.steps_from_previous_finish.length > 0
        ? act.steps_from_previous_finish
        : [DEFAULT_STEP];

    const color = routeColor(raw.uid, raw.color);
    const auxiliarySites: Record<string, UserSiteV1Model> = Object.fromEntries(
      ((auxData?.entitiesArrayPerVersionName['UserSite'] ?? []) as UserSiteV1Model[]).map(
        (site) => [site.uid, site]
      )
    );
    const pickupSiteUidOfJob: Record<string, string> = {};
    raw.actions.forEach((act) => {
      flattenMoves(act.movements)
        .filter((f) => f.kind === 'load' && f.quantity > 1e-10 && !isNil(f.job_uid))
        .forEach((f) => {
          pickupSiteUidOfJob[f.job_uid as string] = act.site_uid;
        });
    });

    const firstAction = raw.actions[0];
    const isLoadedAtStart = !vehicleIsEmpty(firstAction);

    const firstActionSteps = getSteps(firstAction);
    // Compared as elapsed time rather than as a string: the wire spells a zero duration
    // `P0D`, which denotes the same instant as `PT0S`.
    const removeFirstAction =
      firstActionSteps.length === 1 &&
      firstActionSteps[0].kind === 'ActiveService' &&
      parseISO8601Duration(firstActionSteps[0].duration).totalMilliseconds === 0;

    // The walk only knows what a *predecessor* left aboard. When the first action is elided
    // its holdings are that predecessor state; when it is rendered, its own drive leg runs
    // before its service, and what the vehicle arrived with is not recoverable from the
    // post-service holdings, so claim nothing rather than the wrong cargo.
    const initialHoldingsAboard = removeFirstAction ? firstAction.holdings : undefined;

    // Seeded from the first action whether or not it is elided: the temperatures it states
    // are the ones already in force when the route's first drive leg begins, and folding it
    // again when it is rendered upserts the same values.
    const initialTemperatureState = foldTemperatureState(
      [],
      firstAction.movements,
      firstAction.holdings
    );

    const initialState: RouteTimelineState = isLoadedAtStart
      ? {
          nextTaskId: 0,
          lastDashedLine: false,
          holdingsAboard: initialHoldingsAboard,
          temperatureState: initialTemperatureState,
          lastCompartmentsUids: extractCompartmentUids(firstAction.movements)
        }
      : {
          nextTaskId: 0,
          lastDashedLine: true,
          holdingsAboard: initialHoldingsAboard,
          temperatureState: initialTemperatureState,
          lastCompartmentsUids: []
        };

    let {
      nextTaskId,
      lastDashedLine,
      holdingsAboard,
      temperatureState,
      lastCompartmentsUids
    } = initialState;

    const sliceOffset = removeFirstAction ? 1 : 0;
    let actionIndex = 0;
    const middleTasks = raw.actions
      .slice(sliceOffset)
      .flatMap((action: UserRouteActionV1Model): Task[] => {
        let curDate = new Date(action.scheduled_first_step_start_datetime);
        const activeServiceLabels = createActionLabels(action, raw.uid, raw.vehicle_uid);
        const dashedLine = vehicleIsEmpty(action);

        const actionTemperatureState = foldTemperatureState(
          temperatureState,
          action.movements,
          action.holdings
        );
        const currentCompartmentUids = extractCompartmentUids(action.movements);

        const steps = getSteps(action).map((step, stepIndex): Task => {
          const isActiveService = step.kind === 'ActiveService';
          const currentState: RouteTimelineState = {
            nextTaskId,
            lastDashedLine,
            holdingsAboard,
            temperatureState,
            lastCompartmentsUids
          };
          const task = createTaskFromStep(
            step,
            action,
            currentState,
            curDate,
            activeServiceLabels,
            dashedLine,
            currentCompartmentUids,
            actionTemperatureState,
            pickupSiteUidOfJob,
            auxiliarySites,
            raw.uid,
            stepIndex,
            actionIndex
          );
          actionIndex++;
          curDate = task.endDate;
          nextTaskId += 1;
          if (isActiveService) temperatureState = actionTemperatureState;
          return task;
        });

        lastDashedLine = dashedLine;
        lastCompartmentsUids = currentCompartmentUids;
        holdingsAboard = action.holdings;
        return steps;
      });

    const startSite = firstAction.site_uid;
    const endSite = raw.actions[raw.actions.length - 1].site_uid;
    const startDate = new Date(firstAction.scheduled_first_step_start_datetime);

    let firstActionEndDate = new Date(firstAction.scheduled_first_step_start_datetime);
    firstActionSteps.forEach((step) => {
      firstActionEndDate = addDurationFromIso(firstActionEndDate, step.duration);
    });

    const endDate =
      middleTasks.length > 0
        ? middleTasks[middleTasks.length - 1].endDate
        : firstActionEndDate;
    const lastTaskKind: TaskKind =
      middleTasks.length > 0 ? middleTasks[middleTasks.length - 1].kind : 'work';

    const startLabels: VisuItemLabel[] = removeFirstAction
      ? createActionLabels(firstAction, raw.uid, raw.vehicle_uid)
      : [];
    const startTask: Task = {
      id: nextTaskId + 1,
      siteUid: startSite,
      kind: 'start' as TaskKind,
      type: 'basic' as TaskType,
      routeUid: raw.uid,
      name: EntityLabelsTranslationService.translateRouteActionStatic('departure'),
      startDate,
      endDate: firstActionEndDate,
      labels: startLabels,
      highlightAggregatorIds: []
    };

    const lastAction = raw.actions[raw.actions.length - 1];
    const finishLabels: VisuItemLabel[] =
      lastTaskKind === 'drive'
        ? createActionLabels(lastAction, raw.uid, raw.vehicle_uid)
        : [{ label: 'site', level: 0, value: endSite }];
    const finishTask: Task = {
      id: nextTaskId + 2,
      siteUid: endSite,
      kind: 'finish' as TaskKind,
      type: 'basic' as TaskType,
      name: EntityLabelsTranslationService.translateRouteActionStatic('arrival'),
      startDate: endDate,
      endDate,
      labels: finishLabels,
      highlightAggregatorIds: []
    };

    return [
      {
        uid: raw.uid,
        color,
        startDate,
        endDate,
        labels: createRouteKpiLabels(raw.kpis),
        lockCfg: raw.lock_cfg,
        route: raw,
        validated: isRouteValidated(raw),
        name: raw.name ?? '-',
        tasks: [startTask, ...middleTasks, finishTask].map((task) => ({
          ...task,
          siteTimeZone: auxiliarySites[task.siteUid]?.time_zone
        }))
      }
    ];
  },

  buildLists: (raw: UserRouteV1Model): List[] => {
    const color = routeColor(raw.uid, raw.color);
    const items = raw.actions.map(
      (action: UserRouteActionV1Model): ListItem => ({
        appElementId: 0,
        labels: createActionLabels(action, raw.uid, raw.vehicle_uid),
        name: action.uid
      })
    );

    if (items.length <= 0) return [];

    return [
      {
        aggregatorId: raw.uid,
        appElemIds: [],
        color,
        items,
        itemsName: 'actions',
        name: raw.uid
      }
    ];
  },

  // Every site this route stops at, not only those it claims infrastructure at, so a
  // visited site's idle dock bank still surfaces with its declared capacity.
  occupationSiteUids: (raw: UserRouteV1Model): string[] =>
    (raw.actions ?? []).map((action) => action.site_uid)
};

export const routeDetailHandlers: EntityDetailHandlers = {
  getDetailsCfg: (entityType: string): EntityCfg<unknown> | undefined => {
    if (entityType.toLowerCase() === 'route_action') {
      return new UserRouteActionCfg() as EntityCfg<unknown>;
    }
    return undefined;
  },

  getDetails: (entityType: string, raw: unknown): EntityInterface<unknown>[] => {
    if (entityType.toLowerCase() === 'route_action') {
      const route = raw as UserRouteV1Model;
      const cfg = new UserRouteActionCfg();
      return (route.actions ?? []).map(
        (item) => new GenericEntity(item, cfg) as EntityInterface<unknown>
      );
    }
    return [];
  },

  getModificationActions: (raw: unknown): EntityModificationAction[] => {
    const route = raw as UserRouteV1Model;
    const validated = isRouteValidated(route);
    const validationBlocker = routeValidationBlocker(route);
    const frozen = validated
      ? { disabled: true, disabledReasonKey: ROUTE_VALIDATED_REASON_KEY }
      : {};
    return [
      {
        labelKey: 'visualization.timeline.context_menu.lock',
        icon: ICONS.lock,
        entityType: 'route',
        entityTypeVersion: 'UserRoute',
        ...frozen,
        options: ROUTE_LOCK_OPTIONS.map((option) => ({
          labelKey: option.labelKey,
          icon: option.icon,
          isCurrentValue: route.lock_cfg === option.value,
          data: () => ({ uid: route.uid, lock_cfg: option.value })
        }))
      },
      {
        labelKey: 'visualization.timeline.context_menu.company_assignment',
        icon: ICONS.factory,
        entityType: 'route',
        entityTypeVersion: 'UserRoute',
        options: [],
        type: 'multi-entity-select',
        targetEntityType: 'company',
        targetField: 'company_uid',
        currentValue: route.company_uid ?? null,
        dependentsRequireFeature: KNOWN_FEATURE_FLAGS.ENABLE_ROUTE_VEHICLE_REASSIGNMENT,
        ...frozen,
        messageKeys: {
          successTitle: 'visualization.timeline.context_menu.company_update_success_title',
          successMessage:
            'visualization.timeline.context_menu.company_update_success_message',
          errorTitle: 'visualization.timeline.context_menu.company_update_error_title',
          errorMessage: 'visualization.timeline.context_menu.company_update_error_message',
          partialTitle: 'visualization.timeline.context_menu.company_update_partial_title',
          partialMessage:
            'visualization.timeline.context_menu.company_update_partial_message'
        },
        cascade: {
          derive: (source: unknown) => ({
            fields: {
              uid: route.uid,
              name: computeRouteName(route, { companyUid: source as string }),
              company_uid: source as string
            }
          }),
          dependents: {
            entityTypePrefix: 'JOB',
            uids: affectedJobUids(route),
            derive: (dependent: unknown, source: unknown) =>
              deriveJobKpiPatch(
                dependent as { uid: string; kpis?: RawKpi[] },
                'assigned_companies',
                [source as string]
              )
          }
        }
      },
      {
        labelKey: 'visualization.timeline.context_menu.vehicle_assignment',
        icon: ICONS.truck,
        entityType: 'route',
        entityTypeVersion: 'UserRoute',
        options: [],
        type: 'multi-entity-select',
        targetEntityType: 'vehicle',
        targetField: 'vehicle_uid',
        currentValue: route.vehicle_uid,
        requiredFeature: KNOWN_FEATURE_FLAGS.ENABLE_ROUTE_VEHICLE_REASSIGNMENT,
        dependentsRequireFeature: KNOWN_FEATURE_FLAGS.ENABLE_ROUTE_VEHICLE_REASSIGNMENT,
        disabled: validated || hasFleetHandovers(route),
        disabledReasonKey: validated
          ? ROUTE_VALIDATED_REASON_KEY
          : 'visualization.timeline.context_menu.vehicle_reassign_blocked_handover',
        messageKeys: {
          successTitle: 'visualization.timeline.context_menu.vehicle_update_success_title',
          successMessage:
            'visualization.timeline.context_menu.vehicle_update_success_message',
          errorTitle: 'visualization.timeline.context_menu.vehicle_update_error_title',
          errorMessage: 'visualization.timeline.context_menu.vehicle_update_error_message',
          partialTitle: 'visualization.timeline.context_menu.vehicle_update_partial_title',
          partialMessage:
            'visualization.timeline.context_menu.vehicle_update_partial_message'
        },
        cascade: {
          source: { entityType: 'vehicle', entityTypeVersion: 'UserVehicle' },
          sourceNotFoundReasonKey:
            'visualization.timeline.context_menu.vehicle_reassign_vehicle_not_found',
          derive: (source: unknown) =>
            deriveRouteVehicleReassignment(route, source as UserVehicleV1Model),
          dependents: {
            entityTypePrefix: 'JOB',
            uids: affectedJobUids(route),
            derive: (dependent: unknown, source: unknown) =>
              deriveJobKpiPatch(
                dependent as { uid: string; kpis?: RawKpi[] },
                'assigned_vehicles',
                [(source as UserVehicleV1Model).uid]
              )
          }
        }
      },
      {
        labelKey: 'visualization.timeline.context_menu.validation',
        icon: ICONS.checkCircle,
        entityType: 'route',
        entityTypeVersion: 'UserRoute',
        options: ROUTE_VALIDATION_OPTIONS.map((option) => {
          const blocker = option.value === 'Validated' ? validationBlocker : null;
          return {
            labelKey: option.labelKey,
            icon: option.icon,
            isCurrentValue: (route.validation_state ?? 'Unvalidated') === option.value,
            disabled: blocker !== null,
            disabledReasonKey: blocker ?? undefined,
            data: () => ({ uid: route.uid, validation_state: option.value })
          };
        }),
        messageKeys: {
          successTitle:
            'visualization.timeline.context_menu.validation_update_success_title',
          successMessage:
            'visualization.timeline.context_menu.validation_update_success_message',
          errorTitle: 'visualization.timeline.context_menu.validation_update_error_title',
          errorMessage:
            'visualization.timeline.context_menu.validation_update_error_message',
          partialTitle: 'visualization.timeline.context_menu.validation_update_error_title',
          partialMessage:
            'visualization.timeline.context_menu.validation_update_error_message'
        }
      }
    ];
  }
};

export function registerRouteExtensions(registry: GenericEntityRegistryService): void {
  registry.registerOverrides('UserRoute', routeCfgOverrides);
  registry.registerVisualizationBuilders('UserRoute', routeVisualizationBuilders);
  registry.registerDetailHandlers('UserRoute', routeDetailHandlers);
}
